import React, { useCallback, useEffect } from 'react';
import { LocationStore } from '@proscom/prostore-react-router/lib/LocationStore';
import { parseISO } from 'date-fns';
import { FilterSelect } from '../../ui/Filter/FilterSelect';
import { DateRangePickerSeparated } from '../../ui/DatePicker/DateRangePickerSeparated';
import { DateRangePickerValueType } from '../../ui/DatePicker/DateRangePicker';
import {
  TransformedQueryParams,
  URL_KEY_BIRTHDAY_END,
  URL_KEY_BIRTHDAY_START,
  URL_KEY_ABSENCE_DATES_START,
  URL_KEY_ABSENCE_DATES_END,
  URL_KEY_EVENT,
  URL_KEY_PAGE,
  URL_KEY_DISTANT_TYPE,
  URL_KEY_VACATION_TYPE,
  URL_KEY_VACATION_SUBTYPE
} from '../../../../store/urlKeys';
import { formatDate } from '../../../hooks/useFormatDate';
import { UserFilter } from '../../../../graphql/types';
import { DatePickerClasses } from '../../ui/DatePicker/DatePicker';
import { HelpText, HelpTextType } from '../../ui/HelpText/HelpText';
import { ReactComponent as IconInfo } from '../../../../assets/img/icons/Info.svg';
import s from '../../../../routes/vacations/index/VacationSchedules/VacationSchedules.module.scss';

export enum Event {
  birthday = 'birthday',
  vacations = 'vacations',
  dist_work = 'dist_work'
}

const options: { label: string; value: Event }[] = [
  { label: 'Дни рождения', value: Event.birthday },
  { label: 'Отсутствие сотрудников', value: Event.vacations },
  { label: 'Дистанционная работа', value: Event.dist_work }
];

export interface FilterEventProps {
  query: TransformedQueryParams;
  filter: UserFilter;
  changeQuery: LocationStore['changeQuery'];
  handleFilterChange: (
    key: string,
    valueConverter?: (value) => any
  ) => (value: any) => void;
  className?: string;
  datePickerClasses?: DatePickerClasses;
  onSelectEventVacations: () => void;
  onSelectEventDistWork: () => void;
}

export const FilterEvent: React.FC<FilterEventProps> = ({
  query,
  filter,
  changeQuery,
  handleFilterChange,
  className,
  datePickerClasses,
  onSelectEventVacations,
  onSelectEventDistWork
}) => {
  const currentEvent: Event | undefined = query[URL_KEY_EVENT];

  const handleChangeBirthday = useCallback(
    (dates: string[]) => {
      changeQuery({
        [URL_KEY_BIRTHDAY_START]:
          formatDate(dates?.[0], 'yyyy-MM-dd') || undefined,
        [URL_KEY_BIRTHDAY_END]:
          formatDate(dates?.[1], 'yyyy-MM-dd') || undefined,
        [URL_KEY_PAGE]: 1
      });
    },
    [changeQuery]
  );

  const handleChangeAbsenceDates = useCallback(
    (dates: string[]) => {
      changeQuery({
        [URL_KEY_ABSENCE_DATES_START]:
          formatDate(dates?.[0], 'yyyy-MM-dd') || undefined,
        [URL_KEY_ABSENCE_DATES_END]:
          formatDate(dates?.[1], 'yyyy-MM-dd') || undefined,
        [URL_KEY_PAGE]: 1
      });
    },
    [changeQuery]
  );

  useEffect(() => {
    let queryToChange = {};

    // "набираем" изменения, чтобы записать их за один раз
    if (currentEvent !== Event.birthday) {
      queryToChange = {
        [URL_KEY_BIRTHDAY_START]: undefined,
        [URL_KEY_BIRTHDAY_END]: undefined,
        [URL_KEY_PAGE]: 1
      };
    }
    if (currentEvent !== Event.vacations) {
      queryToChange = Object.assign({}, queryToChange, {
        [URL_KEY_VACATION_TYPE]: undefined,
        [URL_KEY_VACATION_SUBTYPE]: undefined,
        [URL_KEY_PAGE]: 1
      });
    }
    if (currentEvent !== Event.dist_work) {
      queryToChange = Object.assign({}, queryToChange, {
        [URL_KEY_DISTANT_TYPE]: undefined,
        [URL_KEY_PAGE]: 1
      });
    }

    if (currentEvent !== Event.vacations && currentEvent !== Event.dist_work) {
      queryToChange = Object.assign({}, queryToChange, {
        [URL_KEY_ABSENCE_DATES_START]: undefined,
        [URL_KEY_ABSENCE_DATES_END]: undefined
      });
    }

    if (Object.keys(queryToChange)) {
      changeQuery(queryToChange);
    }
  }, [changeQuery, currentEvent]);

  return (
    <>
      <FilterSelect
        className={className}
        label="Событие"
        value={currentEvent}
        onChange={(ev) => {
          handleFilterChange(URL_KEY_EVENT)(ev);
          if (ev === Event.dist_work) {
            onSelectEventDistWork();
          }
          if (ev === Event.vacations) {
            onSelectEventVacations();
          }
        }}
        options={options}
        placeholder={'Выберите событие'}
        isClearable
      />

      {currentEvent === Event.birthday && (
        <DateRangePickerSeparated
          className={className}
          classes={datePickerClasses}
          label="Дни рождения"
          startPlaceholder="ДД месяц"
          endPlaceholder="ДД месяц"
          dateFormat="dd MMMM"
          onChange={handleChangeBirthday}
          {...(Boolean(filter.birthday?.start && filter.birthday?.end) && {
            value: [
              filter.birthday?.start,
              filter.birthday?.end
            ] as DateRangePickerValueType
          })}
        />
      )}
      {(currentEvent === Event.vacations ||
        currentEvent === Event.dist_work) && (
        <>
          <HelpText
            className={s.VacationSchedules__hint}
            type={HelpTextType.hint}
            icon={IconInfo}
          >
            Для просмотра информации по{' '}
            {currentEvent === Event.vacations
              ? 'отсутствиям'
              : 'дистанционной работе'}{' '}
            выберите отдел либо ФИО сотрудника
          </HelpText>
          <br />
          <DateRangePickerSeparated
            className={className}
            classes={datePickerClasses}
            label="Даты отсутствия"
            startPlaceholder="ДД месяц"
            endPlaceholder="ДД месяц"
            dateFormat="dd MMMM"
            onChange={handleChangeAbsenceDates}
            minEndDate={
              filter.absence_dates?.start
                ? parseISO(filter.absence_dates.start)
                : null
            }
            {...(Boolean(
              filter.absence_dates?.start && filter.absence_dates?.end
            ) && {
              value: [
                filter.absence_dates?.start,
                filter.absence_dates?.end
              ] as DateRangePickerValueType
            })}
          />
        </>
      )}
    </>
  );
};
